import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

import type { IconName } from '@tutero/ui';

import { SECTION_PAGES, SECTIONS, type SectionId } from './sections';
import { isLink, type LinkTool, type PageTool, type Tool } from './tool';

/**
 * Every tool, from its manifest (`admin-is-an-app` D3): `src/tools/<id>/tool.ts`, found
 * by the build. A tool whose `id` is not its directory's name is a mistake the shell
 * refuses to boot with, since its URL would not be where its owner looks for it.
 */
const manifests = import.meta.glob<{ default: Tool }>('../tools/*/tool.ts', {
  eager: true,
});

/** A page tool, with its page as React loads it: once, the first time it is opened. */
export interface MountedPage extends PageTool {
  Page: LazyExoticComponent<ComponentType>;
}

export type Mounted = MountedPage | LinkTool;

/** Exported for `tools.test.ts`: the boot-time check has no other way in. */
export function mount(path: string, tool: Tool): Mounted {
  const directory = path.split('/').at(-2);
  if (directory !== tool.id) {
    throw new Error(
      `${path} names its tool "${tool.id}"; it must be "${directory}"`,
    );
  }
  if (isLink(tool)) return tool;
  return { ...tool, Page: lazy(tool.routes) };
}

/** The manifests, in whatever order the glob found them: the nav decides the order. */
export const TOOLS: readonly Mounted[] = Object.entries(manifests).map(
  ([path, module]) => mount(path, module.default),
);

/** A section drawn as one row: its open tools are the tabs of its page. */
export interface TabbedPage {
  kind: 'tabs';
  id: SectionId;
  title: string;
  icon: IconName;
  order: number;
  /** Never empty: a page with no open tool is left out, as a section is. */
  tools: [Mounted, ...Mounted[]];
}

/** A row of the nav: a tool, or a page of tabs. */
export type NavRow = Mounted | TabbedPage;

/** One of the nav's groups: a section the session has at least one row in. */
export interface NavSection {
  id: SectionId;
  title: string;
  tools: NavRow[];
}

function inOrder<T extends { order: number; title: string }>(rows: T[]): T[] {
  return rows.sort(
    (a, b) => a.order - b.order || a.title.localeCompare(b.title),
  );
}

/** The pages of tabs `perms` opens anything on. */
function tabbedPages(
  perms: readonly string[],
  tools: readonly Mounted[],
): (TabbedPage & { within: SectionId })[] {
  return SECTION_PAGES.flatMap(({ section, within, order, icon }) => {
    const [first, ...rest] = inOrder(
      tools.filter(
        (tool) => tool.section === section && perms.includes(tool.permission),
      ),
    );
    if (!first) return [];
    const title = SECTIONS.find(({ id }) => id === section)?.title ?? section;
    return [
      {
        kind: 'tabs' as const,
        id: section,
        title,
        icon,
        order,
        within,
        tools: [first, ...rest] as [Mounted, ...Mounted[]],
      },
    ];
  });
}

/**
 * The rows `perms` opens, grouped by `SECTIONS`'s declared order and sorted inside a
 * section by the manifest's `order`, ties falling to title (`manifests-draw-the-nav`
 * D2, D3). Nothing else is sorted: a section with no open row is left out entirely. A
 * section that is one page has no heading: it is one row under the heading it names
 * (`learning-backend-is-one-page` D2).
 */
export function sectionsFor(
  perms: readonly string[],
  tools: readonly Mounted[] = TOOLS,
): NavSection[] {
  const open = tools.filter((tool) => perms.includes(tool.permission));
  const pages = tabbedPages(perms, tools);
  return SECTIONS.filter(
    ({ id }) => !SECTION_PAGES.some(({ section }) => section === id),
  )
    .map(({ id, title }) => ({
      id,
      title,
      tools: inOrder<NavRow>([
        ...open.filter((tool) => tool.section === id),
        ...pages.filter(({ within }) => within === id),
      ]),
    }))
    .filter(({ tools: inside }) => inside.length > 0);
}

/** The page of tabs the open tool `id` is a tab of, or none: most tools stand alone. */
export function tabsAt(
  id: string,
  perms: readonly string[],
  tools: readonly Mounted[] = TOOLS,
): TabbedPage | undefined {
  return tabbedPages(perms, tools).find((page) =>
    page.tools.some((tool) => tool.id === id),
  );
}

/** The page tool at `/<id>`, or none: a `link` has no route and is never the open tool. */
export function pageAt(
  id: string | undefined,
  tools: readonly Mounted[] = TOOLS,
): MountedPage | undefined {
  return tools.find(
    (tool): tool is MountedPage => tool.id === id && !isLink(tool),
  );
}

/** Every grant a manager can give: each tool's own, and its further ones. */
export function grantable(tools: readonly Tool[] = TOOLS): string[] {
  return [
    ...new Set(
      tools.flatMap((tool) => [tool.permission, ...(tool.grants ?? [])]),
    ),
  ].sort();
}
